import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";
import { CONTACT_EMAIL } from "@/lib/api";

const STEPS = [
    {
        num: "01",
        title: "Email us within 15 days",
        body: `Write to ${CONTACT_EMAIL} within 15 days of delivery. Include your order number (it's in your confirmation email) and tell us what's coming back.`,
    },
    {
        num: "02",
        title: "Pack it with care",
        body: "Items must be unused and in their original condition. Wrap your piece the way it arrived — these are handmade, and a little care on the return trip goes a long way.",
    },
    {
        num: "03",
        title: "Send it back",
        body: "We'll reply with the return address and simple shipping instructions. Pop it in the mail and keep your tracking number until the return is complete.",
    },
    {
        num: "04",
        title: "Refund to your card",
        body: "Once your piece arrives back safely, we refund the full purchase amount to your original payment method. It usually appears within 5–10 business days.",
    },
];

const CONDITIONS = [
    "Start your return within 15 days of the delivery date",
    "Items must be unused, unworn, and in their original condition",
    "Include your order number so we can find your purchase",
    "Custom-made pieces are final sale — they're made just for you",
];

export default function ReturnsPage() {
    return (
        <main data-testid="returns-page" className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay">Returns &amp; refunds</p>
                <h1 className="mt-6 max-w-3xl font-serif text-5xl font-light tracking-tighter text-gold md:text-6xl">
                    15 days to <span className="italic text-espresso">change your mind.</span>
                </h1>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-clay">
                    Every Kichazone piece is handmade or hand-picked with care — but if it isn't right for you,
                    we make sending it back simple. No forms, no fuss: just email us and we'll take it from there.
                </p>
            </motion.div>

            <div className="mt-20 grid grid-cols-1 gap-px bg-line md:grid-cols-2">
                {STEPS.map((s, i) => (
                    <motion.div
                        key={s.num}
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: i * 0.08 }}
                        data-testid={`return-step-${s.num}`}
                        className="bg-sand p-10"
                    >
                        <p className="font-serif text-4xl font-light italic text-gold">{s.num}</p>
                        <h2 className="mt-4 font-serif text-2xl font-medium tracking-tight">{s.title}</h2>
                        <p className="mt-3 text-sm leading-relaxed text-clay">{s.body}</p>
                    </motion.div>
                ))}
            </div>

            <div className="mt-20 grid grid-cols-1 gap-12 lg:grid-cols-12">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="border border-line bg-white p-10 lg:col-span-7"
                    data-testid="return-conditions"
                >
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay">The fine print</p>
                    <ul className="mt-6 flex flex-col gap-4">
                        {CONDITIONS.map((c) => (
                            <li key={c} className="flex items-start gap-4 text-sm leading-relaxed text-espresso">
                                <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-gold" />
                                {c}
                            </li>
                        ))}
                    </ul>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="flex flex-col justify-between bg-espresso p-10 text-sand lg:col-span-5"
                >
                    <div>
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-sand/50">Ready when you are</p>
                        <h2 className="mt-4 font-serif text-3xl font-light tracking-tight">
                            Start your <span className="italic text-gold">return.</span>
                        </h2>
                        <p className="mt-4 text-sm leading-relaxed text-sand/60">
                            One email is all it takes — we personally read and answer every one.
                        </p>
                    </div>
                    <div className="mt-10 flex flex-col gap-3">
                        <a
                            href={`mailto:${CONTACT_EMAIL}?subject=Return%20request`}
                            data-testid="returns-start-button"
                            className="group inline-flex items-center justify-center gap-3 bg-gold px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-espresso transition-colors duration-300 hover:bg-sand"
                        >
                            <Mail size={14} />
                            Start a return
                        </a>
                        <Link
                            to="/shop"
                            data-testid="returns-back-to-shop"
                            className="group inline-flex items-center justify-center gap-3 border border-sand/30 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-sand transition-colors duration-300 hover:border-sand hover:bg-sand hover:text-espresso"
                        >
                            Back to the collection
                            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </div>
                </motion.div>
            </div>
        </main>
    );
}
